import { ethers } from 'ethers';

// BSC Mainnet RPC Endpoints (Fallback System)
const BSC_RPC_ENDPOINTS = [
  'https://bsc-dataseed1.binance.org/',
  'https://bsc-dataseed2.binance.org/',
  'https://bsc-dataseed3.binance.org/',
  'https://bsc-dataseed4.binance.org/',
  'https://bsc-dataseed.binance.org/',
  'https://bsc-dataseed1.defibit.io/',
  'https://bsc-dataseed1.ninicoin.io/'
];

// MEV Protection RPC (private mempool, anti-sandwich) - used for submitting trade tx
// PancakeSwap MEV Guard keeps tx out of the public mempool so searcher bots cannot sandwich it
export const MEV_GUARD_RPC = 'https://bscrpc.pancakeswap.finance';

// Contract Addresses
const PANCAKE_ROUTER = '0x10ED43C718714eb63d5aA57B78B54704E256024E';
const WBNB = '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c';

// Ultra Low Fee Configuration
const ULTRA_LOW_FEE_CONFIG = {
  gasPrice: {
    ultraLow: ethers.utils.parseUnits('3', 'gwei'),      // 3 Gwei - Ultra Low
    low: ethers.utils.parseUnits('5', 'gwei'),           // 5 Gwei - Low
    normal: ethers.utils.parseUnits('10', 'gwei'),       // 10 Gwei - Normal
    high: ethers.utils.parseUnits('15', 'gwei'),         // 15 Gwei - High
    emergency: ethers.utils.parseUnits('20', 'gwei')     // 20 Gwei - Emergency
  },
  gasLimit: {
    swap: 180000,        // Optimized: Normal 250000 → 180000
    approve: 50000,      // Optimized: Normal 100000 → 50000
    default: 300000
  },
  maxFeePerTx: ethers.utils.parseEther('0.01'),         // Max 0.01 BNB per TX
  offPeakHours: [1, 2, 3, 4, 5, 6],                    // UTC+8 timezone (gece saatleri)
  waitForLowFees: true,
  onlyOffPeak: false,
  emergencyStop: false,
  batchTrades: false,    // Future: Batch buy+sell in one TX
  privateMempool: false  // Future: MEV Protection
};

// RPC Fallback System
let currentRpcIndex = 0;
let rpcHealthStatus = {};

const getBestRPC = async () => {
  const healthChecks = await Promise.allSettled(
    BSC_RPC_ENDPOINTS.map(async (rpc, index) => {
      try {
        const provider = new ethers.providers.JsonRpcProvider(rpc);
        const startTime = Date.now();
        await provider.getBlockNumber();
        const responseTime = Date.now() - startTime;
        
        rpcHealthStatus[index] = {
          url: rpc,
          healthy: true,
          responseTime,
          lastCheck: Date.now()
        };
        
        return { index, responseTime };
      } catch (error) {
        rpcHealthStatus[index] = {
          url: rpc,
          healthy: false,
          error: error.message,
          lastCheck: Date.now()
        };
        return null;
      }
    })
  );

  const healthyRpcs = healthChecks
    .filter(result => result.status === 'fulfilled' && result.value)
    .sort((a, b) => a.value.responseTime - b.value.responseTime);

  if (healthyRpcs.length > 0) {
    currentRpcIndex = healthyRpcs[0].value.index;
    return BSC_RPC_ENDPOINTS[currentRpcIndex];
  }

  // Fallback to first RPC if all fail
  return BSC_RPC_ENDPOINTS[0];
};

// Enhanced Provider Setup with Fallback
export const setupWeb3 = async (privateKey, useMevGuard = true) => {
  let rpcUrl;
  if (useMevGuard) {
    // Prefer the MEV-protected private RPC for trade submission; fall back to public if it is down
    try {
      const probe = new ethers.providers.JsonRpcProvider(MEV_GUARD_RPC);
      await probe.getBlockNumber();
      rpcUrl = MEV_GUARD_RPC;
    } catch (e) {
      rpcUrl = await getBestRPC();
    }
  } else {
    rpcUrl = await getBestRPC();
  }
  const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
  const wallet = new ethers.Wallet(privateKey, provider);

  return { provider, wallet, rpcUrl };
};

// Read-only public provider (NO private key needed).
// Used for network status, gas, block number and prices so the panel shows
// live data even before the user connects a wallet.
export const getPublicProvider = async () => {
  const rpcUrl = await getBestRPC();
  return new ethers.providers.JsonRpcProvider(rpcUrl);
};

// Smart Contract ABIs
export const ROUTER_ABI = [
  'function swapExactETHForTokens(uint amountOutMin, address[] calldata path, address to, uint deadline) external payable returns (uint[] memory amounts)',
  'function swapExactTokensForETH(uint amountIn, uint amountOutMin, address[] calldata path, address to, uint deadline) external returns (uint[] memory amounts)',
  'function getAmountsOut(uint amountIn, address[] calldata path) external view returns (uint[] memory amounts)',
  'function getAmountsIn(uint amountOut, address[] calldata path) external view returns (uint[] memory amounts)'
];

export const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)',
  'function name() view returns (string)'
];

// Enhanced Gas Price Monitoring
export const getOptimalGasPrice = async (provider) => {
  try {
    const currentGasPrice = await provider.getGasPrice();
    const gasPriceGwei = ethers.utils.formatUnits(currentGasPrice, 'gwei');
    
    let recommendedGasPrice;
    let gasStatus = 'normal';
    
    if (parseFloat(gasPriceGwei) <= 3) {
      recommendedGasPrice = ULTRA_LOW_FEE_CONFIG.gasPrice.ultraLow;
      gasStatus = 'ultraLow';
    } else if (parseFloat(gasPriceGwei) <= 5) {
      recommendedGasPrice = ULTRA_LOW_FEE_CONFIG.gasPrice.low;
      gasStatus = 'low';
    } else if (parseFloat(gasPriceGwei) <= 10) {
      recommendedGasPrice = ULTRA_LOW_FEE_CONFIG.gasPrice.normal;
      gasStatus = 'normal';
    } else if (parseFloat(gasPriceGwei) <= 15) {
      recommendedGasPrice = ULTRA_LOW_FEE_CONFIG.gasPrice.high;
      gasStatus = 'high';
    } else {
      recommendedGasPrice = ULTRA_LOW_FEE_CONFIG.gasPrice.emergency;
      gasStatus = 'emergency';
    }
    
    return {
      current: currentGasPrice,
      recommended: recommendedGasPrice,
      status: gasStatus,
      gwei: parseFloat(gasPriceGwei)
    };
  } catch (error) {
    console.error('Gas price monitoring failed:', error);
    return {
      current: ULTRA_LOW_FEE_CONFIG.gasPrice.normal,
      recommended: ULTRA_LOW_FEE_CONFIG.gasPrice.normal,
      status: 'error',
      gwei: 10
    };
  }
};

// Enhanced Off-Peak Hours Check
export const isOffPeakHours = () => {
  const now = new Date();
  const utc8Hour = (now.getUTCHours() + 8) % 24; // Convert to UTC+8
  return ULTRA_LOW_FEE_CONFIG.offPeakHours.includes(utc8Hour);
};

// Smart Trading Decision
export const shouldExecuteTrade = async (provider, gasSettings = {}) => {
  const {
    onlyOffPeak = ULTRA_LOW_FEE_CONFIG.onlyOffPeak,
    waitForLowFees = ULTRA_LOW_FEE_CONFIG.waitForLowFees,
    maxFeePerTx = ULTRA_LOW_FEE_CONFIG.maxFeePerTx,
    emergencyStop = ULTRA_LOW_FEE_CONFIG.emergencyStop
  } = gasSettings;

  // Emergency stop check
  if (emergencyStop) {
    return { shouldTrade: false, reason: 'Emergency stop activated' };
  }

  // Off-peak hours check
  if (onlyOffPeak && !isOffPeakHours()) {
    return { shouldTrade: false, reason: 'Not off-peak hours' };
  }

  // Gas price check
  const gasInfo = await getOptimalGasPrice(provider);
  const estimatedFee = gasInfo.recommended.mul(ULTRA_LOW_FEE_CONFIG.gasLimit.swap);
  
  // Convert maxFeePerTx to BigNumber if it's a string
  const maxFeeBN = typeof maxFeePerTx === 'string' 
    ? ethers.utils.parseEther(maxFeePerTx) 
    : maxFeePerTx;
  
  if (estimatedFee.gt(maxFeeBN)) {
    return { shouldTrade: false, reason: 'Fee exceeds threshold' };
  }

  // Wait for low fees check
  if (waitForLowFees && gasInfo.status !== 'ultraLow' && gasInfo.status !== 'low') {
    return { shouldTrade: false, reason: 'Waiting for lower gas prices' };
  }

  return { 
    shouldTrade: true, 
    reason: 'All conditions met',
    gasInfo 
  };
};

// Enhanced Balance Functions
export const getRealBNBBalance = async (wallet) => {
  try {
    const balance = await wallet.getBalance();
    return ethers.utils.formatEther(balance);
  } catch (error) {
    console.error('Failed to get BNB balance:', error);
    return '0.000';
  }
};

export const getRealTokenBalance = async (wallet, tokenAddress) => {
  try {
    const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, wallet);
    const balance = await tokenContract.balanceOf(wallet.address);
    const decimals = await tokenContract.decimals();
    return ethers.utils.formatUnits(balance, decimals);
  } catch (error) {
    console.error('Failed to get token balance:', error);
    return '0.000';
  }
};

// Get price of 1 token in BNB.
// DexScreener (CORS-friendly, works on Vercel) first, on-chain PancakeSwap as fallback.
export const getRealTokenPrice = async (provider, tokenAddress) => {
  // 1) DexScreener — public, CORS-enabled, no proxy needed
  try {
    const response = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${tokenAddress}`);
    const data = await response.json();
    if (data.pairs && data.pairs.length) {
      // Prefer the WBNB-quoted pair so priceNative is already in BNB
      const bnbPair = data.pairs.find(p => p.quoteToken && p.quoteToken.symbol === 'WBNB');
      const pair = bnbPair || data.pairs[0];
      if (bnbPair && pair.priceNative) {
        return pair.priceNative; // already BNB per token
      }
      if (pair.priceUsd) {
        const bnbPrice = await getBNBPrice();
        return (parseFloat(pair.priceUsd) / bnbPrice).toString();
      }
    }
  } catch (error) {
    console.log('❌ DexScreener token price failed, trying on-chain:', error);
  }

  // 2) On-chain PancakeSwap quote (most reliable, no external API)
  try {
    const readProvider = provider || (await getPublicProvider());
    const router = new ethers.Contract(PANCAKE_ROUTER, ROUTER_ABI, readProvider);
    const token = new ethers.Contract(tokenAddress, ERC20_ABI, readProvider);
    const decimals = await token.decimals();
    // Quote 1,000,000 tokens to avoid integer-rounding to zero for low-price tokens
    const amountIn = ethers.utils.parseUnits('1000000', decimals);
    const amounts = await router.getAmountsOut(amountIn, [tokenAddress, WBNB]);
    const bnbForMillion = parseFloat(ethers.utils.formatEther(amounts[1]));
    return (bnbForMillion / 1e6).toString();
  } catch (error) {
    console.error('Failed to get token price (on-chain):', error);
    return '0';
  }
};

// Get BNB price in USD
export const getBNBPrice = async () => {
  try {
    // Public, CORS-friendly sources that work directly from the browser (incl. Vercel)
    const sources = [
      'https://api.binance.com/api/v3/ticker/price?symbol=BNBUSDT',
      'https://api.coingecko.com/api/v3/simple/price?ids=binancecoin&vs_currencies=usd',
      'https://api.dexscreener.com/latest/dex/tokens/0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c'
    ];

    for (const source of sources) {
      try {
        const response = await fetch(source);
        const data = await response.json();

        if (source.includes('coingecko')) {
          if (data.binancecoin && data.binancecoin.usd) {
            return data.binancecoin.usd;
          }
        } else if (source.includes('binance')) {
          if (data.price) {
            return parseFloat(data.price);
          }
        } else if (source.includes('dexscreener')) {
          if (data.pairs && data.pairs[0] && data.pairs[0].priceUsd) {
            return parseFloat(data.pairs[0].priceUsd);
          }
        }
      } catch (error) {
        console.log(`❌ Failed to get BNB price from ${source}:`, error);
      }
    }

    throw new Error('BNB price not available from any source');
  } catch (error) {
    console.error('Failed to get BNB price:', error);
    return 300; // Fallback price
  }
};

// Enhanced Network Status
export const getNetworkStatus = async (provider) => {
  try {
    const [blockNumber, gasPrice] = await Promise.all([
      provider.getBlockNumber(),
      provider.getGasPrice()
    ]);
    
    const gasPriceGwei = ethers.utils.formatUnits(gasPrice, 'gwei');
    const isOffPeak = isOffPeakHours();
    const isLowFee = parseFloat(gasPriceGwei) <= 5;
    const isUltraLowFee = parseFloat(gasPriceGwei) <= 3;
    
    return {
      blockNumber,
      gasPrice,
      gasPriceGwei: parseFloat(gasPriceGwei),
      isOffPeak,
      isLowFee,
      isUltraLowFee,
      recommendedGasPrice: await getOptimalGasPrice(provider),
      rpcHealth: rpcHealthStatus,
      currentRpc: BSC_RPC_ENDPOINTS[currentRpcIndex]
    };
  } catch (error) {
    console.error('Failed to get network status:', error);
    return {
      blockNumber: 0,
      gasPrice: ethers.utils.parseUnits('10', 'gwei'),
      gasPriceGwei: 10,
      isOffPeak: false,
      isLowFee: false,
      isUltraLowFee: false,
      recommendedGasPrice: null,
      rpcHealth: {},
      currentRpc: BSC_RPC_ENDPOINTS[0]
    };
  }
};

// Enhanced Ultra Low Fee Trading Functions
export const executeUltraLowFeeBuyOrder = async (wallet, tokenAddress, amountBNB, gasSettings = {}, slippagePercent = 2) => {
  try {
    const { provider } = wallet;
    
    // Check if we should execute trade
    const tradeDecision = await shouldExecuteTrade(provider, gasSettings);
    if (!tradeDecision.shouldTrade) {
      throw new Error(`Trade blocked: ${tradeDecision.reason}`);
    }
    
    const router = new ethers.Contract(PANCAKE_ROUTER, ROUTER_ABI, wallet);
    const path = [WBNB, tokenAddress];
    const amountIn = ethers.utils.parseEther(amountBNB);
    const deadline = Math.floor(Date.now() / 1000) + 1200; // 20 minutes
    
    // Calculate minimum output with slippage
    const amounts = await router.getAmountsOut(amountIn, path);
    const minOutput = amounts[1].mul(100 - slippagePercent).div(100); // slippage protection
    
    // Execute trade with optimized gas settings
    const gasInfo = tradeDecision.gasInfo;
    const tx = await router.swapExactETHForTokens(
      minOutput,
      path,
      wallet.address,
      deadline,
      {
        value: amountIn,
        gasPrice: gasInfo.recommended,
        gasLimit: ULTRA_LOW_FEE_CONFIG.gasLimit.swap
      }
    );
    
    const receipt = await tx.wait();
    const actualFee = receipt.gasUsed.mul(receipt.effectiveGasPrice);
    
    return {
      success: true,
      hash: tx.hash,
      gasUsed: receipt.gasUsed.toString(),
      effectiveGasPrice: receipt.effectiveGasPrice.toString(),
      actualFee: ethers.utils.formatEther(actualFee),
      gasPriceGwei: ethers.utils.formatUnits(receipt.effectiveGasPrice, 'gwei'),
      savings: calculateFeeSavings(actualFee, gasInfo.recommended, receipt.gasUsed)
    };
  } catch (error) {
    console.error('Ultra low fee buy order failed:', error);
    throw error;
  }
};

export const executeUltraLowFeeSellOrder = async (wallet, tokenAddress, amountBNB, gasSettings = {}, slippagePercent = 2) => {
  try {
    const { provider } = wallet;
    
    // Check if we should execute trade
    const tradeDecision = await shouldExecuteTrade(provider, gasSettings);
    if (!tradeDecision.shouldTrade) {
      throw new Error(`Trade blocked: ${tradeDecision.reason}`);
    }
    
    const router = new ethers.Contract(PANCAKE_ROUTER, ROUTER_ABI, wallet);
    const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, wallet);
    const path = [tokenAddress, WBNB];
    
    // Get token balance and calculate sell amount
    const tokenBalance = await tokenContract.balanceOf(wallet.address);
    const tokenDecimals = await tokenContract.decimals();
    
    // Calculate how many tokens equal the BNB amount
    const amounts = await router.getAmountsIn(ethers.utils.parseEther(amountBNB), path);
    const sellAmount = amounts[0];
    
    // Check if we have enough tokens
    if (sellAmount.gt(tokenBalance)) {
      const tokenBalanceFormatted = ethers.utils.formatUnits(tokenBalance, tokenDecimals);
      const sellAmountFormatted = ethers.utils.formatUnits(sellAmount, tokenDecimals);
      throw new Error(`Insufficient token balance for sell. You have ${tokenBalanceFormatted} tokens, but need ${sellAmountFormatted} tokens for ${amountBNB} BNB`);
    }
    
    const deadline = Math.floor(Date.now() / 1000) + 1200; // 20 minutes
    
    // One-time infinite approval: only approve when current allowance is insufficient.
    // Saves ~50k gas on every subsequent sell instead of approving each cycle.
    const currentAllowance = await tokenContract.allowance(wallet.address, PANCAKE_ROUTER);
    if (currentAllowance.lt(sellAmount)) {
      const approveTx = await tokenContract.approve(
        PANCAKE_ROUTER,
        ethers.constants.MaxUint256,
        {
          gasPrice: tradeDecision.gasInfo.recommended,
          gasLimit: ULTRA_LOW_FEE_CONFIG.gasLimit.approve
        }
      );
      await approveTx.wait();
    }
    
    // Calculate minimum BNB output with slippage protection (mirror of buy side)
    const expectedAmounts = await router.getAmountsOut(sellAmount, path);
    const minOutput = expectedAmounts[expectedAmounts.length - 1].mul(100 - slippagePercent).div(100); // slippage protection

    // Execute sell
    const tx = await router.swapExactTokensForETH(
      sellAmount,
      minOutput, // Slippage-protected minimum, prevents sandwich/MEV drain
      path,
      wallet.address,
      deadline,
      {
        gasPrice: tradeDecision.gasInfo.recommended,
        gasLimit: ULTRA_LOW_FEE_CONFIG.gasLimit.swap
      }
    );
    
    const receipt = await tx.wait();
    const actualFee = receipt.gasUsed.mul(receipt.effectiveGasPrice);
    
    return {
      success: true,
      hash: tx.hash,
      gasUsed: receipt.gasUsed.toString(),
      effectiveGasPrice: receipt.effectiveGasPrice.toString(),
      actualFee: ethers.utils.formatEther(actualFee),
      gasPriceGwei: ethers.utils.formatUnits(receipt.effectiveGasPrice, 'gwei'),
      savings: calculateFeeSavings(actualFee, tradeDecision.gasInfo.recommended, receipt.gasUsed)
    };
  } catch (error) {
    console.error('Ultra low fee sell order failed:', error);
    throw error;
  }
};

// Fee Savings Calculator
export const calculateFeeSavings = (actualFee, recommendedGasPrice, gasUsed) => {
  try {
    const normalGasPrice = ULTRA_LOW_FEE_CONFIG.gasPrice.normal;
    const normalFee = normalGasPrice.mul(gasUsed);
    const savings = normalFee.sub(actualFee);
    
    return {
      normalFee: ethers.utils.formatEther(normalFee),
      actualFee: ethers.utils.formatEther(actualFee),
      savings: ethers.utils.formatEther(savings),
      savingsPercentage: ((savings.mul(100).div(normalFee)).toNumber())
    };
  } catch (error) {
    console.error('Fee savings calculation failed:', error);
    return {
      normalFee: '0.000',
      actualFee: '0.000',
      savings: '0.000',
      savingsPercentage: 0
    };
  }
};

// Transaction Status Checker
export const getTransactionStatus = async (provider, txHash) => {
  try {
    const receipt = await provider.getTransactionReceipt(txHash);
    return {
      confirmed: receipt && receipt.confirmations > 0,
      confirmations: receipt ? receipt.confirmations : 0,
      status: receipt ? (receipt.status === 1 ? 'success' : 'failed') : 'pending',
      gasUsed: receipt ? receipt.gasUsed.toString() : '0',
      effectiveGasPrice: receipt ? receipt.effectiveGasPrice.toString() : '0'
    };
  } catch (error) {
    console.error('Failed to get transaction status:', error);
    return {
      confirmed: false,
      confirmations: 0,
      status: 'error',
      gasUsed: '0',
      effectiveGasPrice: '0'
    };
  }
};

// Backward compatibility aliases
export const executeRealBuyOrder = executeUltraLowFeeBuyOrder;
export const executeRealSellOrder = executeUltraLowFeeSellOrder;
export const getRealGasPrice = getOptimalGasPrice; 